import { useMutation, useLazyQuery } from '@apollo/client';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { GET_ME } from '../graphql/getMe';
import {
  GetQuestionsPaginatedQuery,
  Question,
  Role,
} from '../__generated__/graphql';
import DOMPurify from 'dompurify';
import { DELETE_QUESTION } from '../graphql/deleteQuestion';
import { QuestionBankStyles } from '../styles/QuestionBankStyles';
import { tagToColor } from '../types/Tag';
import { labelToColor } from '../types/Label';
import { QuizStyles } from '../styles/QuizStyles';
import { CommonStyles } from '../styles/CommonStyles';
import SearchBar from '../components/SearchBar';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigateHome } from '../hooks/useNavigateHome';
import { GET_QUESTIONS_PAGINATED } from '../graphql/getQuestionsPaginated';
import { SEARCH_QUESTIONS_PAGINATED } from '../graphql/searchQuestionsPaginated';

const getChipColor = (tag: string): string => {
  return tagToColor[tag] || '#BDBDBD';
};

const getLabelColor = (label: string): string => {
  return labelToColor[label] || '#6c757d';
};

const QuestionBank = () => {
  const navigate = useNavigate();
  const [getMe, { data: me, loading: meLoading }] = useLazyQuery(GET_ME);
  const { navigateHome } = useNavigateHome();
  const [deleteQuestion] = useMutation(DELETE_QUESTION);

  // Pagination state
  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(
    undefined,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);

  // Refs for infinite scroll
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastQuestionRef = useRef<HTMLDivElement | null>(null);

  const PAGE_SIZE = 10;

  // Queries
  const [getQuestionsPaginated] = useLazyQuery<GetQuestionsPaginatedQuery>(
    GET_QUESTIONS_PAGINATED,
    {
      onCompleted: (data) => {
        if (data?.getQuestionsPaginated) {
          const { questions, hasMore: more } = data.getQuestionsPaginated;
          setDisplayedQuestions((prev) =>
            currentPage === 0 ? questions : [...prev, ...questions],
          );
          setHasMore(more);
          setIsLoading(false);
        }
      },
      onError: () => {
        setIsLoading(false);
      },
    },
  );

  const [searchQuestionsPaginated] = useLazyQuery(SEARCH_QUESTIONS_PAGINATED, {
    onCompleted: (data) => {
      if (data?.searchQuestionsPaginated) {
        const { questions, hasMore: more } = data.searchQuestionsPaginated;
        setDisplayedQuestions((prev) =>
          currentPage === 0 ? questions : [...prev, ...questions],
        );
        setHasMore(more);
        setIsLoading(false);
      }
    },
    onError: () => {
      setIsLoading(false);
    },
  });

  // Load initial questions
  useEffect(() => {
    getMe();
    loadQuestions();
  }, [getMe]);

  // Load questions function
  const loadQuestions = useCallback(
    async (page = 0, search = '', label?: string) => {
      setIsLoading(true);
      setCurrentPage(page);

      if (search || label) {
        searchQuestionsPaginated({
          variables: {
            query: search,
            tag: label,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
          },
        });
      } else {
        getQuestionsPaginated({
          variables: { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
        });
      }
    },
    [getQuestionsPaginated, searchQuestionsPaginated],
  );

  // Load more questions
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      loadQuestions(nextPage, searchQuery, selectedLabel);
    }
  }, [
    isLoading,
    hasMore,
    currentPage,
    searchQuery,
    selectedLabel,
    loadQuestions,
  ]);

  // Intersection observer for infinite scroll
  const lastQuestionRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (lastQuestionRef.current) {
        lastQuestionRef.current = null;
      }

      if (node) {
        lastQuestionRef.current = node;
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoading) {
            loadMore();
          }
        });

        observerRef.current.observe(node);
      }
    },
    [hasMore, isLoading, loadMore],
  );

  // Handle search
  const handleSearch = useCallback(
    (query: string, label?: string) => {
      setSearchQuery(query);
      setSelectedLabel(label);
      setCurrentPage(0);
      setHasMore(true);
      loadQuestions(0, query, label);
    },
    [loadQuestions],
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  if (meLoading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  const handleDeleteClick = (id: string) => {
    setQuestionToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (questionToDelete) {
      await deleteQuestion({
        variables: { id: questionToDelete },
        update(cache) {
          // Remove from local state instead of cache since we're not using the old query
          setDisplayedQuestions((prev) =>
            prev.filter((q) => q.id !== questionToDelete),
          );
        },
      });
      setDeleteDialogOpen(false);
      setQuestionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setQuestionToDelete(null);
  };

  return (
    <Box sx={QuestionBankStyles.container}>
      <Typography sx={QuestionBankStyles.header}>Question Bank</Typography>
      <SearchBar onSearchResults={handleSearch} />
      {!displayedQuestions?.length && !isLoading && (
        <Box sx={QuizStyles.noContent}>No questions found</Box>
      )}
      {displayedQuestions.map((question, index) => (
        <Box
          key={question.id}
          sx={QuestionBankStyles.questionContainer}
          ref={
            index === displayedQuestions.length - 1
              ? lastQuestionRefCallback
              : null
          }
        >
          <Box sx={QuestionBankStyles.chipsContainer}>
            <Chip
              label={question.tag}
              sx={{
                backgroundColor: getChipColor(question.tag),
                color: '#FFF',
                fontSize: '0.75rem',
                borderRadius: '12px',
              }}
            />
            {question.label && (
              <Chip
                label={question.label}
                sx={{
                  backgroundColor: getLabelColor(question.label),
                  color: '#FFF',
                  fontSize: '0.75rem',
                  borderRadius: '12px',
                }}
              />
            )}
          </Box>
          <Box sx={QuestionBankStyles.promptContainer}>
            <Box
              sx={QuestionBankStyles.prompt}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(question.prompt),
              }}
            />
            <IconButton
              sx={{ color: 'rgb(57, 73, 142)' }}
              onClick={() =>
                navigate(`/admin/questions/${question.id}`, {
                  state: { question },
                })
              }
              size="small"
              aria-label="Edit Question"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              sx={{ color: 'rgb(57, 73, 142)' }}
              onClick={() => handleDeleteClick(question.id)}
              size="small"
              aria-label="Delete Question"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={40} />
        </Box>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Question</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this question?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="primary"
            variant="contained"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestionBank;
